// FormWorkflowModal — opened from the Forms-page "Workflow" button. Two views in
// one Modal: the list (POST /workflows/all — search, Workflows/Archived filter
// chips, DataTable + pagination) and the create builder (New workflow →
// WorkflowBuilder). "Responses" opens the response-tags modal; each row carries
// a kebab whose flows all live here — Duplicate, Version history (the modal the
// builder's toolbar also opens, reachable now without entering an editable
// surface) and the three lifecycle moves, which are one PATCH apart.
//
// The two tabs carry DIFFERENT action sets:
//   Workflows → Duplicate · Version history · Archive
//   Archived  → Version history · Restore · Delete
// and the Archived tab is otherwise READ-ONLY — no row click into the builder,
// so Restore is the way back to editing. Version history is on both tabs and
// ungated, which is also what keeps the kebab from ever rendering empty.
//
// The status filter is a Chip pair, not Tabs, per the design — brand for the
// active one, outline for the other. Chip renders a <span>, so this is a
// mouse-only control; that trade was made deliberately, don't "fix" it back to
// Tabs.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Button,
  Chip,
  ConfirmModal,
  DataTable,
  Icon,
  IconButton,
  Input,
  Modal,
  TableFooter,
} from '@/components/common';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useModuleAccess from '@/hooks/useModuleAccess';
import useStickyTotal from '@/hooks/useStickyTotal';
import {
  useDuplicateWorkflowMutation,
  useGetWorkflowsQuery,
  useSetWorkflowStatusMutation,
  type WorkflowItem,
  type WorkflowStatusTarget,
} from '@/api/endpoints/workflowsApi';
import {
  useCreateWorkflowResponseTagMutation,
  useDeleteWorkflowResponseTagMutation,
  useGetWorkflowResponseTagsQuery,
  useUpdateWorkflowResponseTagMutation,
} from '@/api/endpoints/tagsApi';
import TagsModal, { type TagDraft } from '@/pages/structure-page/TagsModal';
import { useWorkflowColumns } from './workflowColumns';
import WorkflowBuilder from './WorkflowBuilder';
import WorkflowVersionsModal from './WorkflowVersionsModal';

interface FormWorkflowModalProps {
  open: boolean;
  onClose: () => void;
}

type WorkflowTab = 'workflows' | 'archived';
type WorkflowView = 'list' | 'builder';

/** Which toast each lifecycle destination reports — the ACTION_TOAST idiom
 *  from FormTemplatesPage. Keyed by the move target, so a new status member
 *  would be a type error here rather than a silently missing toast. */
const STATUS_TOAST: Record<WorkflowStatusTarget, string> = {
  active: 'forms_workflow_restore_done',
  archived: 'forms_workflow_archive_done',
  deleted: 'forms_workflow_delete_done',
};

const FormWorkflowModal = ({ open, onClose }: FormWorkflowModalProps) => {
  const { t } = useTranslation();
  const [view, setView] = useState<WorkflowView>('list');
  const [editId, setEditId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<WorkflowTab>('workflows');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  // Which row's version history is open — ONE modal instance for the whole
  // list, not one per row. Holds the row itself so the modal gets both the id
  // and the version number without a second lookup.
  const [versionsFor, setVersionsFor] = useState<WorkflowItem | null>(null);
  // An Archive or a Delete awaiting its ConfirmModal. Restore and Duplicate
  // fire straight away — only the destructive moves confirm
  // (FormTemplatesPage.tsx:107, and TrashPage restores without one either).
  const [confirmArchive, setConfirmArchive] = useState<WorkflowItem | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<WorkflowItem | null>(null);

  // Response tags — fetched while the workflow modal is open so they're ready
  // by the time the user clicks "Responses" (TagsModal re-seeds only on open).
  const { data: responseTags = [] } = useGetWorkflowResponseTagsQuery(undefined, { skip: !open });
  const [createResponseTag] = useCreateWorkflowResponseTagMutation();
  const [updateResponseTag] = useUpdateWorkflowResponseTagMutation();
  const [deleteResponseTag] = useDeleteWorkflowResponseTagMutation();

  // Reset to a clean state each time the modal (re)opens.
  useEffect(() => {
    if (open) {
      setView('list');
      setEditId(null);
      setActiveTab('workflows');
      setPage(1);
      setSearch('');
      setVersionsFor(null);
      setConfirmArchive(null);
      setConfirmDelete(null);
    }
  }, [open]);

  const { data, currentData, isLoading, isError, isFetching } = useGetWorkflowsQuery(
    {
      status: activeTab === 'workflows' ? 'active' : 'archived',
      search: debouncedSearch || undefined,
      page,
      limit,
    },
    { skip: !open, refetchOnMountOrArgChange: true },
  );

  // A new search / tab shrinks the result set — snap back to page 1.
  useEffect(() => setPage(1), [debouncedSearch, activeTab]);

  // The workflow surface is its OWN module (`workflow` slug) — verbs come from
  // it, not from forms_templates (the button opening this modal already needs
  // workflow `view`). The response-tags Save mixes create/rename/delete into
  // one call, so the whole Responses flow rides `edit`.
  const { canCreate, canEdit, canDelete } = useModuleAccess('workflow');

  const items = data?.items ?? [];
  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  const archivedTab = activeTab === 'archived';

  const [setWorkflowStatus, { isLoading: acting }] = useSetWorkflowStatusMutation();
  const [duplicateWorkflow] = useDuplicateWorkflowMutation();

  // Archive / Restore / Delete — one runner for all three, since they are the
  // same call with a different destination. Returns success so a ConfirmModal
  // can stay open on failure, the runAction idiom from FormTemplatesPage.
  const runStatus = async (id: string, status: WorkflowStatusTarget): Promise<boolean> => {
    try {
      await setWorkflowStatus({ id, status }).unwrap();
      toast.success(t(STATUS_TOAST[status]));
      // The row leaves this tab, so a page holding only that row would strand
      // the user on an empty one — the templates table's step-back guard,
      // reduced to the single-row case.
      if (page > 1 && items.length === 1) setPage((p) => p - 1);
      return true;
    } catch {
      toast.error(t('error_generic'));
      return false;
    }
  };

  const runDuplicate = async (id: string) => {
    try {
      await duplicateWorkflow(id).unwrap();
      toast.success(t('forms_workflow_duplicate_done'));
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // The two tabs carry different action sets. Version history is the one item
  // on both — and ungated, which is what stops the Archived kebab emptying for
  // a role holding neither edit nor delete.
  const columns = useWorkflowColumns({
    onVersions: setVersionsFor,
    onDuplicate: canCreate && !archivedTab ? (w) => void runDuplicate(w.id) : undefined,
    onArchive: canEdit && !archivedTab ? setConfirmArchive : undefined,
    onRestore: canEdit && archivedTab ? (w) => void runStatus(w.id, 'active') : undefined,
    onDelete: canDelete && archivedTab ? setConfirmDelete : undefined,
  });

  // Persist the response-tags draft — create new rows, rename changed ones,
  // delete server tags dropped from the draft (all concurrently).
  const saveTags = async (draft: TagDraft[]) => {
    const byId = new Map(responseTags.map((tag) => [tag.id, tag.title]));
    const draftIds = new Set(draft.filter((d) => d.id).map((d) => d.id));
    const creates = draft.filter((d) => !d.id);
    const updates = draft.filter((d) => d.id && byId.get(d.id) !== d.title);
    const deletes = responseTags.filter((tag) => !draftIds.has(tag.id));

    setSavingTags(true);
    try {
      await Promise.all([
        ...creates.map((d) => createResponseTag({ title: d.title }).unwrap()),
        ...updates.map((d) => updateResponseTag({ id: d.id as string, title: d.title }).unwrap()),
        ...deletes.map((tag) => deleteResponseTag(tag.id).unwrap()),
      ]);
      setTagsOpen(false);
      toast.success(t('forms_workflow_responses_saved'));
    } catch {
      toast.error(t('error_generic'));
    } finally {
      setSavingTags(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        className='form-workflow'
        icon='workflow'
        title={t('forms_workflow_title')}
        size='lg'
        // The builder holds unsaved work — a stray outside click must not close it.
        closeOnOverlay={view !== 'builder'}
      >
        {view === 'builder' ? (
          <WorkflowBuilder workflowId={editId ?? undefined} onExit={() => setView('list')} />
        ) : (
          <>
            <div className='form-workflow__toolbar'>
              <div className='form-workflow__search'>
                <Input
                  size='sm'
                  iconStart='search'
                  placeholder={t('forms_workflow_search_ph')}
                  aria-label={t('forms_workflow_search_ph')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <IconButton
                    className='form-workflow__search-clear'
                    icon='x'
                    variant='ghost'
                    size='sm'
                    aria-label={t('forms_search_clear')}
                    onClick={() => setSearch('')}
                  />
                )}
              </div>
              <div className='form-workflow__actions'>
                {canEdit && (
                  <Button
                    variant='secondary'
                    iconStart='list-checks'
                    onClick={() => setTagsOpen(true)}
                  >
                    {t('forms_workflow_action_responses')}
                  </Button>
                )}
                {canCreate && (
                  <Button
                    iconStart='plus'
                    onClick={() => {
                      setEditId(null);
                      setView('builder');
                    }}
                  >
                    {t('forms_workflow_action_new')}
                  </Button>
                )}
              </div>
            </div>

            {/* Status filter — brand chip is the active one, outline the other.
                The count sits on the ACTIVE chip only: the endpoint returns a
                total for the status it was asked for, so there is no number for
                the other. Bare figure, hence no i18n key. */}
            <div className='form-workflow__filters'>
              <Chip
                size='lg'
                variant={activeTab === 'workflows' ? 'brand' : 'outline'}
                onClick={() => setActiveTab('workflows')}
              >
                {t('forms_workflow_tab_workflows')}
                {activeTab === 'workflows' && total > 0 && ` ${total}`}
              </Chip>
              <Chip
                size='lg'
                variant={activeTab === 'archived' ? 'brand' : 'outline'}
                onClick={() => setActiveTab('archived')}
              >
                {t('forms_workflow_tab_archived')}
                {activeTab === 'archived' && total > 0 && ` ${total}`}
              </Chip>
            </div>

            <DataTable
              columns={columns}
              rows={items}
              rowKey={(w) => w.id}
              // The Archived tab is read-only: an archived workflow must not
              // open the builder, whose Save draft / Publish are live. Restore
              // is the way back to editing, and the row's Version history
              // keeps it inspectable meanwhile. Undefined drops DataTable's
              // click affordance along with the handler.
              onRowClick={
                canEdit && !archivedTab
                  ? (w) => {
                      setEditId(w.id);
                      setView('builder');
                    }
                  : undefined
              }
              loading={isLoading || isFetching}
              error={isError}
              loadingText={t('text_loading')}
              errorText={t('error_generic')}
              minWidth={800}
              empty={
                <>
                  <Icon name='workflow' size={26} />
                  {t('forms_workflow_empty')}
                </>
              }
            />

            <TableFooter
              page={page}
              limit={limit}
              total={total}
              limits={[10, 20, 50]}
              onPage={setPage}
              onLimit={(n) => {
                setLimit(n);
                setPage(1);
              }}
              perPageLabel={t('forms_workflow_items_per_page')}
              rangeText={t('forms_workflow_range', { from, to, total })}
            />
          </>
        )}
      </Modal>

      <TagsModal
        open={tagsOpen}
        tags={responseTags}
        saving={savingTags}
        onClose={() => setTagsOpen(false)}
        onSave={saveTags}
        title={t('forms_workflow_responses_title')}
        note={t('forms_workflow_responses_note')}
        emptyText={t('forms_workflow_responses_empty')}
        placeholder={t('forms_workflow_responses_placeholder')}
        createLabel={t('forms_workflow_responses_create')}
        saveLabel={t('forms_workflow_responses_save')}
      />

      {/* One instance driven by the chosen row. Mounted only while a row is
          chosen: the versions query skips while closed and is
          keepUnusedDataFor:0, so each open refetches. */}
      {versionsFor && (
        <WorkflowVersionsModal
          open
          workflowId={versionsFor.id}
          currentVersion={versionsFor.versionNumber}
          onClose={() => setVersionsFor(null)}
        />
      )}

      {/* Stays open on failure — runStatus reports. */}
      {confirmArchive && (
        <ConfirmModal
          open
          onClose={() => setConfirmArchive(null)}
          onConfirm={async () => {
            if (await runStatus(confirmArchive.id, 'archived')) setConfirmArchive(null);
          }}
          tone='warning'
          icon='archive'
          title={t('forms_workflow_archive_confirm_title')}
          description={t('forms_workflow_archive_confirm_body')}
          confirmLabel={t('forms_action_archive')}
          loading={acting}
        />
      )}

      {/* Archived tab only. Danger-toned, unlike Archive: `status: 'deleted'`
          is a soft delete server-side, but TrashItemType carries no workflow
          member, so nothing in the UI can bring it back — the copy says so. */}
      {confirmDelete && (
        <ConfirmModal
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={async () => {
            if (await runStatus(confirmDelete.id, 'deleted')) setConfirmDelete(null);
          }}
          tone='danger'
          icon='trash-2'
          title={t('forms_workflow_delete_confirm_title', { name: confirmDelete.name })}
          description={t('forms_workflow_delete_confirm_body')}
          confirmLabel={t('button_delete')}
          confirmVariant='danger'
          loading={acting}
        />
      )}
    </>
  );
};

export default FormWorkflowModal;
